import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { io, Socket } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || '';

export interface School {
  id: string;
  email: string;
  schoolName: string;
  contactPerson: string | null;
  phone: string | null;
  approved: boolean;
  createdAt: string;
  lastLogin: string | null;
  updatedAt?: string;
}

export function useSchools() {
  const { token, user } = useAuth();
  const [schools, setSchools] = useState<School[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('all');

  const fetchSchools = useCallback(async () => {
    if (!token || user?.role !== 'admin') return;

    try {
      const response = await fetch(`${API_URL}/api/users/schools`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSchools(data);
      }
    } catch (error) {
      console.error('Failed to fetch schools:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token, user?.role]);

  useEffect(() => {
    if (!token || user?.role !== 'admin') {
      setIsLoading(false);
      return;
    }

    // Setup socket connection for real-time updates
    const socket: Socket = io(API_URL || window.location.origin);

    socket.on('new-school-registration', (school: School) => {
      setSchools(prev => [...prev, school]);
    });

    socket.on('school-updated', (updatedSchool: School) => {
      setSchools(prev => prev.map(s => s.id === updatedSchool.id ? updatedSchool : s));
    });

    socket.on('school-deleted', ({ id }: { id: string }) => {
      setSchools(prev => prev.filter(s => s.id !== id));
    });

    fetchSchools();

    return () => {
      socket.disconnect();
    };
  }, [token, user?.role, fetchSchools]);

  const updateSchool = async (schoolId: string, updates: Partial<School>) => {
    if (!token) return;

    const response = await fetch(`${API_URL}/api/users/${schoolId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update school');
    }

    return response.json();
  };

  const approveSchool = async (schoolId: string) => {
    return updateSchool(schoolId, { approved: true });
  };

  const rejectSchool = async (schoolId: string) => {
    return updateSchool(schoolId, { approved: false });
  };

  const deleteSchool = async (schoolId: string) => {
    if (!token) return;

    const response = await fetch(`${API_URL}/api/users/${schoolId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete school');
    }

    return response.json();
  };

  // Filtered schools based on current filter
  const filteredSchools = schools.filter(school => {
    if (filter === 'pending') return !school.approved;
    if (filter === 'approved') return school.approved;
    return true;
  });

  // Count for badges
  const pendingCount = schools.filter(s => !s.approved).length;

  return {
    schools: filteredSchools,
    allSchools: schools,
    isLoading,
    filter,
    setFilter,
    pendingCount,
    approveSchool,
    rejectSchool,
    deleteSchool,
    refetch: fetchSchools
  };
}
